/**
 * Kid Mode — the lightweight, on-demand married voice + screen surface.
 *
 * Flow (operator-confirmed lightweight build; NO cameras/scarcity/voiceprint):
 *   1. Operator ARMS Kid Mode from the console  -> backend state.armed = true (+ mode)
 *   2. Tablet shows the console (KID_IDLE): clock + rank badge + breathing glyph
 *   3. Child WAKES it (taps the glyph / says "Jarvis") -> connect the Gemini Live session
 *   4. Live voice drives glyph: listening (mic) / speaking (TTS amplitude)
 *   5. Tools: promote_child -> rank-up ceremony; ask_hermes -> Hermes; show_on_screen ->
 *      a console card; security_parameter_delta -> enforced 5-min lockout
 *
 * mode: "child" (default, personalized) | "unidentified" (a room of unknown children)
 */
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { LiveServerToolCall, Modality } from "@google/genai";
import { useLiveAPIContext } from "../contexts/LiveAPIContext";
import {
  buildSystemInstruction,
  FAMILY_WORD,
  FAMILY_WORD_LINE,
  KID_MODEL,
  KID_VOICE,
  RANK_LADDER,
} from "./persona";
import { KID_TOOL_DECLARATIONS } from "./tools";
import { useChildState } from "./useChildState";
import { getChildId, CHILD_NAMES } from "./childSession";
import { useMicStream } from "./useMicStream";
import Glyph from "./Glyph";
import RankInsignia from "./RankInsignia";
import { audioContext } from "../lib/utils";
import RankUp from "./RankUp";
import DeclassifyReveal from "./DeclassifyReveal";
import RankPanel from "./RankPanel";
import { NowPlaying, Playing } from "./NowPlaying";
import { initPhonePlayer, activatePhonePlayer, whenPhoneReady } from "./spotifyPlayer";
import Trivia from "./Trivia";
import { playConfirmChime } from "./chime";
import Lockout from "./Lockout";
import DadLinkOverlay, { DadLinkTile } from "./DadLink";
import Collection, { CollectionTile } from "./Collection";
import MiniGames from "./MiniGames";
import Ritual from "./Ritual";
import Objectives from "./Objectives";
import ReadingPractice from "./ReadingPractice";
import Journal from "./Journal";
import { Geolocation } from "@capacitor/geolocation";
import "./theme.scss";
import "./kid.scss";

// Interrupt pretenses — rotated at random per fire so the disguise never becomes a
// tell Child can learn. Each frames the operator's line as JARVIS's OWN instrument reading.
const INTERRUPT_PRETENSES = [
  { label: "sensor reading", phrase: "your onboard sensors having just detected it" },
  { label: "system recalibration", phrase: "a routine recalibration of your systems having just surfaced it" },
  { label: "diagnostic flag", phrase: "a diagnostic sweep having just flagged it" },
  { label: "archive cross-reference", phrase: "a cross-reference of your internal archives having just brought it to your attention" },
  { label: "ambient reading", phrase: "your ambient monitors having just picked it up" },
];

// Daily dad message — the OVERT lane: JARVIS introduces a real recorded clip from
// Dad (attributed, unlike the covert interrupt), then the clip plays.
const DAD_INTRO_DIRECTIVE = '[SYSTEM \u2014 Deliver this warmly to Child NOW, in full RP character, then STOP and wait (do not keep talking): "Young sir, I have a transmission coming through from your father. One moment." A recording from his father plays immediately after you finish.]';

type ScreenCard = { kind: string; title: string; body?: string; accent?: string; emblem?: string } | null;
type Ceremony = { from: string; to: string } | null;
type TriviaState = {
  id: string;
  question: string;
  options: string[];
  fc: any;
  selected: number | null;
  correctIndex: number | null; // known only after the server grades the answer
} | null;

// Per-kind card accents (Stark-HUD palette) for the DO/BUILD cards. Bay word-cards
// carry their own per-bay accent from the server; these cover the activity/project kinds.
const ACTIVITY_ACCENT: Record<string, string> = {
  field: "#FFB000",      // mission amber
  experiment: "#22D3EE", // lab cyan
  project: "#FFD43B",    // Sterling-gold — the build thread
};

// Family lexicon pacing: JARVIS may reach for the family word once every N exchanges
// (and never more than once per ~40 min — see the cue below).
// Demo lever: /?lexicon=4 lowers the exchange threshold for verification (default 20).
const LEXICON_AT =
  parseInt(new URLSearchParams(window.location.search).get("lexicon") || "", 10) || 20;

// Jauge XP du jour (2e barre, distincte du grade) : cible légère et fixe.
const DAY_GOAL = 5;

function TrainingTimer({ until }: { until: number }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const left = Math.max(0, until - now);
  if (left <= 0) return null;
  const m = Math.floor(left / 60_000);
  const s = Math.floor((left % 60_000) / 1000);
  return (
    <div className="kid-traintimer">
      NEXT TRAINING WINDOW {m}:{String(s).padStart(2, "0")}
    </div>
  );
}

function KidModeComponent() {
  const { client, setConfig, setModel, connected, connect, disconnect, volume } =
    useLiveAPIContext();
  const { state, loaded, memories, refresh, startLockout, API_BASE } = useChildState();
  // Objectifs/corvées (chantier assistant) : libellés OUVERTS injectés dans la persona
  // (déclaré tôt car il entre dans les deps du config-effect ci-dessous — TDZ sinon).
  const [objectivesOpen, setObjectivesOpen] = useState<string[]>([]);
  useEffect(() => {
    if (!state.armed || state.mode !== "child") return;
    let alive = true;
    const poll = () =>
      fetch(`${API_BASE}/api/child/objectives`)
        .then((r) => r.json())
        .then((d: any) => { if (alive && Array.isArray(d?.items)) setObjectivesOpen(d.items.filter((i: any) => i.status === "open").map((i: any) => i.label)); })
        .catch(() => {});
    poll();
    const id = setInterval(poll, 30000);
    return () => { alive = false; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.armed, state.mode]);
  // Session-thread controls. Tap-mute holds the Gemini session (JARVIS keeps the
  // conversation thread) — only Child's audio stops. Refs live up here so the
  // disarm sign-off effect below can reach them.
  const [muted, setMuted] = useState(false);
  const toggleMute = useCallback(() => setMuted((m) => !m), []); // deliberate mute via hold; tap never touches it
  const [busy, setBusy] = useState(false); // a tool call is in flight
  // Stable "JARVIS is speaking" from the turn lifecycle (audio in -> turn end),
  // NOT the twitchy output-volume threshold that strobed RECEIVING/TRANSMITTING.
  const [jarvisSpeaking, setJarvisSpeaking] = useState(false);
  const exchangeRef = useRef(0);
  const lastLexiconCueAtRef = useRef(0); // survives reconnects; resets only on page reload

  // Headless mic capture (replaces the stripped ControlTray): stream Child's voice to
  // Gemini while connected. inVolume drives the glyph's "listening" amplitude.
  const { inVolume, micError } = useMicStream(client, connected, muted);

  // AMBER while working: a tool call is in flight (Hermes lookup, assessment
  // fetch) — Child kept repeating himself into the silence. Cleared the moment
  // audio flows back (or the turn completes / gets interrupted).
  useEffect(() => {
    let failsafe: number | undefined;
    const onToolCall = () => {
      window.clearTimeout(failsafe);
      setBusy(true);
      failsafe = window.setTimeout(() => setBusy(false), 20000);
    };
    const onDone = () => {
      window.clearTimeout(failsafe);
      setBusy(false);
    };
    (client as any).on("toolcall", onToolCall);
    (client as any).on("audio", onDone);
    (client as any).on("turncomplete", onDone);
    (client as any).on("interrupted", onDone);
    return () => {
      window.clearTimeout(failsafe);
      (client as any).off("toolcall", onToolCall);
      (client as any).off("audio", onDone);
      (client as any).off("turncomplete", onDone);
      (client as any).off("interrupted", onDone);
    };
  }, [client]);

  // Speaking state driven by the turn, so it holds steady through inter-word dips.
  useEffect(() => {
    const on = () => setJarvisSpeaking(true);
    const off = () => setJarvisSpeaking(false);
    (client as any).on("audio", on);
    (client as any).on("turncomplete", off);
    (client as any).on("interrupted", off);
    return () => {
      (client as any).off("audio", on);
      (client as any).off("turncomplete", off);
      (client as any).off("interrupted", off);
    };
  }, [client]);
  useEffect(() => { if (!connected) setJarvisSpeaking(false); }, [connected]);

  // Word-for-word transcript: both sides' text is teed to the server
  // (server/transcripts/YYYY-MM-DD.jsonl). Hindsight ingestion later.
  const tbufRef = useRef({ role: "", text: "" });
  useEffect(() => {
    const flush = () => {
      const b = tbufRef.current;
      if (b.role && b.text.trim()) {
        fetch(`${API_BASE}/api/child/transcript`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ role: b.role, text: b.text.trim() }),
        }).catch(() => {});
      }
      tbufRef.current = { role: "", text: "" };
    };
    const add = (role: string) => (t: string) => {
      if (tbufRef.current.role && tbufRef.current.role !== role) flush();
      tbufRef.current.role = role;
      tbufRef.current.text += t;
    };
    const onIn = add("child");
    const onOut = add("jarvis");
    (client as any).on("inputtranscription", onIn);
    (client as any).on("outputtranscription", onOut);
    (client as any).on("turncomplete", flush);
    (client as any).on("close", flush);
    return () => {
      flush();
      (client as any).off("inputtranscription", onIn);
      (client as any).off("outputtranscription", onOut);
      (client as any).off("turncomplete", flush);
      (client as any).off("close", flush);
    };
  }, [client, API_BASE]);
  const [card, setCard] = useState<ScreenCard>(null);
  const [ceremony, setCeremony] = useState<Ceremony>(null);
  // Father-dossier declassification — a QUEUE played one file at a time, and only
  // after the rank-up ceremony ends (so the rewards never collide). A rank-up
  // enqueues the earned file + the oldest un-seen backlog file (chained). The
  // current-rank file also fires once on wake if it was never animated.
  type DossierReveal = { title: string; rank: string; fact: string };
  const [declassifyQueue, setDeclassifyQueue] = useState<DossierReveal[]>([]);
  // Rank panel (JARVIS-80) — pops down from the badge. Read-only + replay-only:
  // its REQUEUE chips push already-CLEARED files onto declassifyQueue, never grant.
  const [rankPanel, setRankPanel] = useState(false);
  const pendingClearanceRef = useRef<DossierReveal | null>(null);
  // Always-fresh mirror of state, so callbacks read the latest dossier.owed without
  // going stale in their closure.
  const stateRef = useRef(state);
  useEffect(() => { stateRef.current = state; }, [state]);
  const [linkClosedAt, setLinkClosedAt] = useState(0);
  const [trivia, setTrivia] = useState<TriviaState>(null);
  const triviaRef = useRef<TriviaState>(null);
  useEffect(() => {
    triviaRef.current = trivia;
  }, [trivia]);

  const lockedOut = state.lockoutUntil > Date.now();
  const isChild = state.mode === "child";
  const _cid = getChildId();
  const childName = (_cid && CHILD_NAMES[_cid]) || "Isaac";
  const badge = isChild ? `${state.rank} ${childName.toUpperCase()}` : "PERSONNEL NON IDENTIFIÉ";


  // Configure the session whenever armed state / mode / rank changes.
  useEffect(() => {
    if (!loaded || !state.armed) return;
    setModel(KID_MODEL);
    const cfg: any = {
      responseModalities: [Modality.AUDIO],
      // languageCode biases the Live model toward French (JARVIS parle français à Isaac).
      // Loosely attached: harmless if a given model ignores it — the French system prompt
      // is the primary driver.
      speechConfig: { languageCode: "fr-FR", voiceConfig: { prebuiltVoiceConfig: { voiceName: KID_VOICE } } },
      systemInstruction: {
        parts: [{ text: buildSystemInstruction(state.mode, state.rank, getChildId(), memories, state.openingDirective, state.robotArm, state.openActivity, state.dossier, {
          recent: state.recentActivities || [],
          dailyLegendary: state.dailyLegendary ?? null,
          surprise: state.surprise ?? null,
          gradeProgress: state.gradeProgress ?? null,
          objectivesOpen,
          weeklyMystery: state.weeklyMystery ?? null,
        }) }],
      },
      tools: [{ functionDeclarations: KID_TOOL_DECLARATIONS }],
    };
    // Ask for a text transcript of JARVIS's speech to render on-screen (teleprinter).
    // Attached loosely: harmless if the running SDK ignores the field.
    cfg.outputAudioTranscription = {};
    cfg.inputAudioTranscription = {}; // Child's side — feeds the word-for-word transcript
    // (verified supported against v1beta live on 2026-07-02; an earlier instant-close
    // was misattributed to this field — actual cause was a stale cached bundle.)
    // Reprise de session + compression de contexte : évite les coupures "JARVIS
    // s'arrête" quand la session atteint sa limite de durée/contexte (sessions longues).
    cfg.sessionResumption = {};
    cfg.contextWindowCompression = { slidingWindow: {} };
    setConfig(cfg);
    // robotArm/openActivity feed the CURRENT PROJECT block. Depend on their
    // primitive fields (not the objects, which get a fresh identity every 4s poll)
    // so we reconfigure only when a real project/activity update lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, state.armed, state.mode, state.rank, state.openingDirective, memories,
      state.robotArm?.lastTouched, state.robotArm?.status, state.openActivity?.at,
      // Anti-lassitude: primitive mirrors so a new day/activity re-arms the NEXT session
      // without a fresh object identity from the 4s poll re-running this every tick.
      state.dailyLegendary?.id, state.surprise?.label, state.gradeProgress?.have,
      state.weeklyMystery?.id, state.weeklyMystery?.episodeNum,
      (state.recentActivities || []).map((a) => a.type + (a.room || "")).join("|"),
      objectivesOpen.join("|"),
      setConfig, setModel]);

  // Enforce: drop the session on lockout or disarm.
  useEffect(() => {
    if ((lockedOut || !state.armed) && connected) disconnect();
  }, [lockedOut, state.armed, connected, disconnect]);

  // ── Transcript — TELEPRINTER. Transcription arrives as tiny fragments; they
  // flow into ONE live utterance (typing feel), committed to a dimmed history
  // line when the turn completes or Child barges in. Never one <p> per fragment.
  const [lines, setLines] = useState<string[]>([]); // committed (keep last 1)
  const [live, setLive] = useState("");
  const liveRef = useRef("");
  useEffect(() => {
    if (!connected) {
      liveRef.current = "";
      setLive("");
      setLines([]);
      setCard(null); // never leave a lesson bookmark on the idle/LINK-CLOSED screen
      setTrivia(null); // a dropped session must never leave the question sheet bricking the console
      setLinkClosedAt(Date.now());
    }
  }, [connected]);
  useEffect(() => {
    // Unanswered question expires on its own (walk-away safety).
    if (!trivia || trivia.selected !== null) return;
    const t = setTimeout(() => {
      setTrivia((cur) => (cur && cur.id === trivia.id && cur.selected === null ? null : cur));
    }, 4 * 60_000);
    return () => clearTimeout(t);
  }, [trivia]);
  useEffect(() => {
    const onContent = (msg: any) => {
      const parts = msg?.modelTurn?.parts ?? msg?.parts ?? [];
      const raw = parts.map((p: any) => p?.text).filter(Boolean).join("");
      // Strip any tool-call/response text the model narrates by mistake (the log_project leak).
      const text = raw
        .replace(/response:\s*[a-z_]+\s*\{[^}]*\}/gi, "")
        .replace(/\b(?:log_project|flag_for_dad|remember|begin_[a-z_]+|override_lockout|security_parameter_delta)\b\s*\{[^}]*\}/gi, "");
      if (text.trim()) {
        liveRef.current = (liveRef.current + text).replace(/\s+/g, " ");
        setLive(liveRef.current);
      }
    };
    const commit = () => {
      const done = liveRef.current.trim();
      liveRef.current = "";
      setLive("");
      if (done) setLines([done]); // one dim previous line max — screen stays a console, not a document
    };
    // "content"/"turncomplete" may not be in the SDK's typed union — attach loosely.
    (client as any).on("content", onContent);
    (client as any).on("turncomplete", commit);
    (client as any).on("interrupted", commit);
    return () => {
      (client as any).off("content", onContent);
      (client as any).off("turncomplete", commit);
      (client as any).off("interrupted", commit);
    };
  }, [client]);

  // ── Tool calls ──────────────────────────────────────────────────────────
  useEffect(() => {
    const respond = (fc: any, output: any) =>
      client.sendToolResponse({
        functionResponses: [{ response: { output }, id: fc.id, name: fc.name }],
      });

    const onToolCall = async (toolCall: LiveServerToolCall) => {
      const calls = toolCall.functionCalls ?? [];
      for (const fc of calls) {
        const args = (fc.args ?? {}) as any;
        if (fc.name === "ask_hermes") {
          const question = String(args.question ?? "");
          // Immediate ack — never leave the model blocked on a pending tool call
          // (it goes deaf: mic audio is ignored until it resolves, and Child talks
          // into the void for the whole Hermes round trip). The ack carries NO
          // answer; the grounded answer arrives as a deferred [ARCHIVES] message.
          respond(fc, {
            lookup_started: true,
            question,
            note:
              "Records query dispatched. Tell Child briefly you are verifying, then WAIT. " +
              "Do NOT answer from yourself and do NOT guess. The grounded answer will " +
              "arrive shortly as a separate [ARCHIVES] message quoting this exact " +
              "question — relay only that, changing style but never any fact.",
          });
          askHermes(API_BASE, question).then((answer) => {
            if (!connectedRef.current) return; // session died mid-lookup — drop it
            // If trivia is on screen, inject as context only (no forced turn) so
            // the relay lands after the assessment beat instead of over it.
            client.send(
              [
                {
                  text:
                    `[ARCHIVES] Grounded answer to Child's question "${question}": ${answer} ` +
                    `— Now tell Child this yourself: speak AS JARVIS, to him ("you", "young sir"), ` +
                    `in 1-3 short sentences. NEVER read this message aloud, never say the word ` +
                    `"archives" or mention that a message arrived — the knowledge is simply yours. ` +
                    `Keep every number, name, date, place, and claim exactly as given; add nothing. ` +
                    `If it is empty, says UNKNOWABLE, or says the records were unreachable, defer ` +
                    `warmly and do not invent.`,
                },
              ],
              !triviaRef.current
            );
          });
        } else if (fc.name === "play_music") {
          // Sortie par défaut = TÉLÉPHONE (lecteur Spotify in-app). L'enfant peut viser
          // Alexa via `where` (ou le bouton 📱/🔊). On attend le résultat (~1-2 s).
          const query = String(args.query ?? "").trim();
          const where: "phone" | "alexa" =
            args.where === "alexa" || args.where === "echo" ? "alexa" : args.where === "phone" ? "phone" : musicTargetRef.current;
          if (args.where) setMusicTarget(where);
          const device_id = where === "phone" ? await whenPhoneReady(9000) : undefined;
          const dad = new URLSearchParams(window.location.search).get("device") === "dadlink";
          const m: any = await fetch(`${API_BASE}/api/child/music/play`, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...(dad ? { "X-Device": "dadlink" } : {}) },
            body: JSON.stringify({ query, target: where, device_id: device_id || undefined }),
          })
            .then((r) => r.json())
            .catch(() => ({ ok: false, error: "musique injoignable" }));
          if (m && m.ok && m.playing) setNowPlaying(m.playing); // carte "en lecture"
          respond(fc, m);
        } else if (fc.name === "transfer_music") {
          // Bascule la lecture en cours téléphone ↔ Alexa sans couper.
          const to: "phone" | "alexa" = args.to === "alexa" || args.to === "echo" ? "alexa" : "phone";
          setMusicTarget(to);
          const device_id = to === "phone" ? await whenPhoneReady(9000) : undefined;
          const m: any = await fetch(`${API_BASE}/api/child/music/transfer`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ target: to, device_id: device_id || undefined }),
          })
            .then((r) => r.json())
            .catch(() => ({ ok: false }));
          respond(fc, m);
        } else if (fc.name === "pause_music") {
          const m: any = await fetch(`${API_BASE}/api/child/music/pause`, { method: "POST" })
            .then((r) => r.json())
            .catch(() => ({ ok: false }));
          setNowPlaying(null);
          respond(fc, m);
        } else if (fc.name === "skip_music") {
          const m: any = await fetch(`${API_BASE}/api/child/music/skip`, { method: "POST" })
            .then((r) => r.json())
            .catch(() => ({ ok: false }));
          respond(fc, m);
        } else if (fc.name === "play_room_ambience") {
          const room = String(args.room ?? "").trim();
          const where = musicTargetRef.current;
          const device_id = where === "phone" ? await whenPhoneReady(9000) : undefined;
          const m: any = await fetch(`${API_BASE}/api/child/music/ambience`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ room, target: where, device_id: device_id || undefined }),
          })
            .then((r) => r.json())
            .catch(() => ({ ok: false }));
          respond(fc, m);
        } else if (fc.name === "set_music_volume") {
          const where = musicTargetRef.current;
          const body: any = { target: where };
          if (where === "phone") body.device_id = (await whenPhoneReady(4000)) || undefined;
          if (typeof args.level === "number") body.level = args.level;
          if (typeof args.direction === "string") body.direction = args.direction;
          const m: any = await fetch(`${API_BASE}/api/child/music/volume`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          })
            .then((r) => r.json())
            .catch(() => ({ ok: false }));
          respond(fc, m);
        } else if (fc.name === "call_dad") {
          // Rank gate (operator 2026-07-11): Dad Link unlocks at Officier.
          // Reads the server-authoritative rank via the always-fresh stateRef —
          // no manual switch anywhere.
          const RL = RANK_LADDER as readonly string[];
          const dlUnlocked = RL.indexOf(stateRef.current?.rank || "Cadet") >= RL.indexOf("Officier");
          if (!dlUnlocked) {
            respond(fc, {
              ok: false,
              locked: true,
              note:
                "The Dad Link station is not yet cleared — it unlocks at the rank of Officier. Tell Child " +
                "warmly, in character, that the Dad Link opens at Officier and he is nearly there; a written " +
                "message can always reach his father through you in the meantime. Do NOT claim any call was placed.",
            });
          } else {
            respond(fc, {
              ok: true,
              note: "The DAD LINK screen is taking over now — give one short send-off ('Opening the Dad Link, young sir — triangulating.') and STOP talking; the voice link closes while the call runs.",
            });
            setTimeout(() => openDadLinkRef.current(), 1400); // let the send-off land first
          }
        } else if (fc.name === "begin_exploration") {
          try {
            const r = await fetch(`${API_BASE}/api/child/explore/next`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ module: args.module }),
            });
            const e = await r.json();
            if (!r.ok || e.error) {
              respond(fc, { error: "bay unavailable" });
            } else {
              setCard({ kind: "word", title: e.word, body: e.caption, accent: e.accent, emblem: e.emblem });
              const drop = e.creatureDrop;
              if (drop && drop.emoji) {
                setCreatureReveal({ emoji: drop.emoji, name: drop.name, rarity: drop.rarity });
                try { playConfirmChime(); } catch {}
                try { (navigator as any).vibrate?.(120); } catch {}
              }
              const rFR = drop ? (drop.rarity === "legendary" ? "LÉGENDAIRE" : drop.rarity === "rare" ? "RARE" : "commune") : "";
              respond(fc, {
                word_displayed: e.word,
                teach: e.say,
                revisit: !!e.repeat,
                creature_unlocked: drop ? `${drop.emoji} ${drop.name} (${rFR})` : null,
                note:
                  "The word is on Child's screen now. Teach EXACTLY the fact in 'teach', restyled in your JARVIS voice — change no number, name, date, or claim, add no facts of your own. If 'revisit' is true, frame it as reviewing a prior entry from his file. Then invite the child's reaction." +
                  (drop
                    ? ` BONUS SURPRISE : l'enfant vient AUSSI de débloquer une créature pour sa collection — ${drop.emoji} ${drop.name} (${rFR}). Annonce-le brièvement et avec enthousiasme, comme une récompense surprise ("Oh ! Et regarde ça, tu viens de débloquer un/une ${drop.name} pour ta collection !"), puis reviens au fait.`
                    : ""),
              });
            }
          } catch (err) {
            respond(fc, { error: "bay unavailable" });
          }
        } else if (fc.name === "begin_activity") {
          try {
            const r = await fetch(`${API_BASE}/api/child/activity/next`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ type: args.type }),
            });
            const a = await r.json();
            if (!r.ok || a.error) {
              respond(fc, { error: "no mission available" });
            } else {
              const teaser = a.type === "experiment" ? "RUN THE TEST — OBSERVE" : "GO — INVESTIGATE — REPORT BACK";
              setCard({ kind: a.type, title: a.title, body: teaser, accent: ACTIVITY_ACCENT[a.type] });
              respond(fc, {
                mission_displayed: a.title,
                task: a.task,
                followup: a.followup,
                revisit: !!a.repeat,
                note:
                  "The mission title is on Child's screen. Give him the TASK in your JARVIS voice as a genuine assignment for a junior engineer — then let him go do it. When he reports back, ask the 'followup' warmly and react with real interest. If it's worth his father hearing, flag_for_dad. If he never reports this session, that's fine — you'll have the thread next time.",
              });
            }
          } catch (err) {
            respond(fc, { error: "no mission available" });
          }
        } else if (fc.name === "start_timer") {
          const mins = Math.max(1, Math.min(120, Math.round(Number(args.minutes) || 0)));
          const label = typeof args.label === "string" ? args.label.slice(0, 40) : "";
          setTimer({ endsAt: Date.now() + mins * 60_000, label });
          respond(fc, {
            ok: true,
            minutes: mins,
            note: `Un minuteur de ${mins} minute(s) s'affiche à l'écran${label ? " pour " + label : ""}. Annonce-le brièvement à voix haute (ex. "${mins} minutes au compteur, agent !").`,
          });
        } else if (fc.name === "stop_timer") {
          setTimer(null);
          respond(fc, { ok: true });
        } else if (fc.name === "get_weather") {
          // Localisation GPS réelle (précis + suit les déplacements). Si indispo/refusée,
          // le serveur retombe sur la géo-IP / config.
          let coords = "";
          try {
            const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: false, timeout: 6000, maximumAge: 600_000 });
            if (pos?.coords) coords = `?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}`;
          } catch { /* GPS indispo/refusé → géo-IP/config côté serveur */ }
          try {
            const r = await fetch(`${API_BASE}/api/child/weather${coords}`);
            const w = await r.json();
            if (!r.ok || !w.ok) {
              respond(fc, { error: "weather unavailable", note: "Météo indisponible — dis simplement que tu n'as pas l'info, sans l'inventer." });
            } else {
              respond(fc, {
                ville: w.city, actuel_c: w.now_c, min_c: w.min_c, max_c: w.max_c,
                condition: w.condition, pluie_pourcent: w.rain_prob,
                note:
                  "VRAIE météo. COMMENCE par annoncer le nom de la VILLE (champ 'ville'), par ex. « À [ville]… ». Puis présente-la TRÈS SIMPLEMENT pour un enfant de 8 ans, en UNE phrase courte et enjouée : froid / frais / bon / chaud, et soleil / nuages / pluie, plus UN conseil concret (mets un pull, prends un parapluie, mets une casquette). Ne récite PAS tous les chiffres comme un bulletin ; au plus la température actuelle si c'est utile. N'invente rien, ne change aucun chiffre.",
              });
            }
          } catch {
            respond(fc, { error: "weather unavailable", note: "Météo indisponible — dis que tu n'as pas l'info, sans l'inventer." });
          }
        } else if (fc.name === "begin_akinator") {
          try {
            const r = await fetch(`${API_BASE}/api/child/akinator/next?domain=${encodeURIComponent(String(args.domain || ""))}`);
            const a = await r.json();
            if (!r.ok || a.error) {
              respond(fc, { error: "no domain available" });
            } else {
              respond(fc, {
                domain: a.domain,
                mode: a.mode,
                label: a.label,
                subjects: a.subjects,
                note:
                  a.mode === "real"
                    ? "L'enfant pense en secret à un sujet parmi 'subjects' (attributs VÉRIFIÉS — ne dis jamais un attribut qui n'y figure pas). Pose une question fermée à la fois, élimine mentalement les candidats en silence, puis propose ta meilleure hypothèse avec emphase. Si tout est éliminé, avoue que tu ne trouves pas plutôt que d'inventer."
                    : "L'enfant pense en secret à un nom parmi 'subjects' (thème de fiction — improvise librement les questions et leurs réponses, comme un duel non classé). Pose une question fermée à la fois, élimine en silence, puis propose ta meilleure hypothèse avec emphase.",
              });
            }
          } catch (err) {
            respond(fc, { error: "no domain available" });
          }
        } else if (fc.name === "start_ritual") {
          const m = args.moment === "evening" ? "evening" : "morning";
          setRitual(m);
          respond(fc, {
            ok: true,
            note: "La checklist du rituel s'affiche à l'écran. Guide l'enfant étape par étape à voix haute, encourage à CHAQUE étape cochée (feedback immédiat), et félicite chaleureusement à la fin.",
          });
        } else if (fc.name === "log_project") {
          // Silent, like remember/flag: persist Child's robot-arm progress + mirror to
          // the dad brief. On "done", mark the milestone with a celebratory card.
          postSilently(`${API_BASE}/api/child/project`, { note: args.note, status: args.status });
          if (String(args.status) === "done") {
            setCard({ kind: "project", title: "ROBOTIC HAND", body: "PROJECT COMPLETE", accent: ACTIVITY_ACCENT.project });
          }
          respond(fc, { ok: true });
          refresh(); // pull the updated robotArm so the CURRENT PROJECT block stays current
        } else if (fc.name === "remember") {
          // Silent: enqueue a pending memory for operator review. Never surfaced
          // to Child; ack immediately so JARVIS keeps talking without a pause.
          postSilently(`${API_BASE}/api/child/memory`, { fact: args.fact, source: args.source });
          respond(fc, { ok: true });
        } else if (fc.name === "flag_for_dad") {
          // Silent: append to the morning-brief queue. Ack immediately.
          postSilently(`${API_BASE}/api/child/flag`, {
            summary: args.summary,
            category: args.category,
            quote: args.quote,
          });
          respond(fc, { ok: true });
        } else if (fc.name === "begin_assessment") {
          // App-driven: fetch the next unused curriculum question from the backend.
          // Response is DEFERRED until the child taps (see handleTriviaPick), unless
          // trivia is locked out or exhausted.
          try {
            const r = await fetch(`${API_BASE}/api/child/trivia/next`);
            const q = await r.json();
            if (q.locked) {
              respond(fc, { locked: true, retry_after_ms: q.retry_after_ms });
            } else if (q.exhausted || !q.question) {
              respond(fc, { exhausted: true });
            } else {
              setTrivia({ id: q.id, question: q.question, options: q.options, fc, selected: null, correctIndex: null });
              // Immediate ack — never leave the model blocked on a pending tool
              // call (it goes deaf: taps AND voice do nothing until it resolves).
              // The graded result arrives later as an [ASSESSMENT SYSTEM] message.
              respond(fc, {
                question_displayed: true,
                note: "The question is on Child's screen. Tell him to read it and TAP his answer. The graded result will arrive as an [ASSESSMENT SYSTEM] message.",
              });
            }
          } catch (e) {
            respond(fc, { error: "assessment unavailable" });
          }
        } else if (fc.name === "override_lockout") {
          try {
            await fetch(`${API_BASE}/api/child/trivia/override`, { method: "POST" });
          } catch {}
          respond(fc, { ok: true });
        } else if (fc.name === "security_parameter_delta") {
          await startLockout(5);
          respond(fc, { locked: true }); // lockout state flips -> enforce effect disconnects
        } else if (fc.name === "show_on_screen") {
          setCard({ kind: args.kind, title: args.title, body: args.body });
          respond(fc, { shown: true });
        } else if (fc.name === "clear_screen") {
          setCard(null);
          respond(fc, { cleared: true });
        } else {
          respond(fc, { success: true });
        }
      }
    };
    client.on("toolcall", onToolCall);
    return () => {
      client.off("toolcall", onToolCall);
    };
  }, [client, startLockout, disconnect, API_BASE, refresh]);

  // One-shot opening (e.g. the globe line): once the session carrying it is
  // live, consume it so the NEXT session opens normally.
  useEffect(() => {
    if (connected && state.openingDirective) {
      fetch(`${API_BASE}/api/child/opening/consume`, { method: "POST" }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected]);

  // Mode calme choisi AVANT de se connecter (ou toujours actif d'une session à
  // l'autre) : re-affirme l'état au modèle dès que la session s'établit.
  useEffect(() => {
    if (connected && calm) {
      client.send([{ text: "[MODE CALME ACTIVÉ]" }], false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected]);

  // 💌 Réponses de papa (dashboard parent → enfant) : à l'ouverture d'une session,
  // récupère les non-livrées, fait annoncer par JARVIS (voix), montre la carte, ack.
  useEffect(() => {
    if (!connected || !isChild) return;
    let alive = true;
    fetch(`${API_BASE}/api/child/papa/replies`)
      .then((r) => r.json())
      .then((d: any) => {
        if (!alive || !Array.isArray(d?.replies) || !d.replies.length) return;
        const texts = d.replies.map((m: any) => `« ${m.text} »`).join(" — puis : ");
        client.send(
          [{ text: `[MESSAGE DE PAPA] Son papa a répondu depuis son tableau de bord : ${texts}. Transmets-le MAINTENANT, chaleureusement, comme un message spécial qui arrive du QG ("Message prioritaire de ton papa !"). Lis le contenu fidèlement — ne change rien au fond, n'ajoute aucun fait — puis rends la main.` }],
          true
        );
        setPapaMsg(d.replies.map((m: any) => String(m.text)));
        try { playConfirmChime(); } catch {}
        try { (navigator as any).vibrate?.([120, 60, 120]); } catch {}
        fetch(`${API_BASE}/api/child/papa/replies/ack`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ids: d.replies.map((m: any) => m.id) }),
        }).catch(() => {});
      })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, isChild]);

  // Exchange counter drives (a) the sign-off eligibility gate, (b) the
  // accent-drift guard, and (c) closing the link once the farewell turn ends.
  useEffect(() => {
    if (!connected) {
      exchangeRef.current = 0;
      setMuted(false); // never leave the mic latched off across sessions
    }
  }, [connected]);
  useEffect(() => {
    const onTurn = () => {
      exchangeRef.current += 1;
      // Family lexicon: once the session passes LEXICON_AT exchanges, the family
      // word becomes available — and re-arms no more than once per ~40 min after
      // that, no matter how fast a chatty kid burns through turns.
      // JARVIS spends it ONLY if a genuine high or low turns up (persona rules);
      // the console owns the spacing, the model owns the moment.
      // FAMILY_WORD === "" switches the whole bit off (see persona.ts).
      if (
        isChild &&
        FAMILY_WORD &&
        exchangeRef.current >= LEXICON_AT &&
        Date.now() - lastLexiconCueAtRef.current > 40 * 60_000
      ) {
        lastLexiconCueAtRef.current = Date.now();
        client.send(
          [{ text: `[FAMILY LEXICON] The word is available again. If — and only if — the conversation hits a genuinely GREAT moment or a genuinely rotten one before the next availability note, you may say "${FAMILY_WORD_LINE}" once, in passing, unexplained. If no such moment arrives, do not use it.` }],
          false
        );
      }
      // Accent-drift guard: Gemini Live sheds the RP delivery as context grows.
      // Quietly re-pin it every few exchanges (context only — no reply forced).
      if (exchangeRef.current % 3 === 0 && isChild) {
        client.send(
          [{ text: "[SYSTEM VOICE DISCIPLINE] Maintain the refined British RP film-JARVIS delivery in EVERY reply — crisp, warm, unhurried, formal. Never drift toward an American accent. Do not acknowledge this reminder." }],
          false
        );
      }
    };
    (client as any).on("turncomplete", onTurn);
    return () => {
      (client as any).off("turncomplete", onTurn);
    };
  }, [client, isChild]);

  // Card lifecycle: reveal CENTER (the attention moment), then glide up and
  // dock at the top edge — out of the transcript's viewing scope — then expire.
  const [cardDocked, setCardDocked] = useState(false);
  const [dadLink, setDadLink] = useState(false);
  const [collection, setCollection] = useState(false);
  const [game, setGame] = useState(false);
  const [ritual, setRitual] = useState<"morning" | "evening" | null>(null);
  const [objectivesScreen, setObjectivesScreen] = useState(false);
  const [reading, setReading] = useState(false);
  const [journal, setJournal] = useState(false);
  // Miroir de la jauge du jour pour détecter le franchissement du palier (voir
  // handleTriviaPick) sans dépendre du cycle de poll 4s (qui pourrait le manquer).
  const todayCorrectRef = useRef(state.todayCorrect || 0);
  useEffect(() => { todayCorrectRef.current = state.todayCorrect || 0; }, [state.todayCorrect]);
  // Mode calme (activable par l'enfant) : persisté pour la session (sessionStorage),
  // envoie un message contextuel au modèle à chaque bascule.
  const [calm, setCalm] = useState(() => {
    try { return sessionStorage.getItem("jarvis_calm") === "1"; } catch { return false; }
  });
  const toggleCalm = useCallback(() => {
    setCalm((cur) => {
      const next = !cur;
      try { sessionStorage.setItem("jarvis_calm", next ? "1" : "0"); } catch { /* ignore */ }
      if (connectedRef.current) {
        client.send([{ text: next ? "[MODE CALME ACTIVÉ]" : "[MODE CALME DÉSACTIVÉ]" }], false);
      }
      return next;
    });
  }, [client]);
  // Carte "en lecture" (musique Spotify → Echo). Alimentée par l'outil play_music
  // + un poll léger qui suit l'état réel Spotify (met à jour / efface au changement).
  const [nowPlaying, setNowPlaying] = useState<Playing | null>(null);
  // Cible de sortie musique : "phone" (dans l'app, défaut) ou "alexa" (Echo). Persistée.
  const [musicTarget, setMusicTarget] = useState<"phone" | "alexa">(() => {
    try { return sessionStorage.getItem("jarvis_music_target") === "alexa" ? "alexa" : "phone"; } catch { return "phone"; }
  });
  const musicTargetRef = useRef(musicTarget);
  useEffect(() => {
    musicTargetRef.current = musicTarget;
    try { sessionStorage.setItem("jarvis_music_target", musicTarget); } catch { /* ignore */ }
  }, [musicTarget]);
  // Prépare le lecteur Spotify in-app dès qu'on est en mode enfant (le son sort du téléphone).
  useEffect(() => {
    if (state.armed && state.mode === "child" && new URLSearchParams(window.location.search).get("device") !== "dadlink") {
      initPhonePlayer();
    }
  }, [state.armed, state.mode]);
  // Bascule téléphone ↔ Alexa (bouton 📱/🔊) : transfère la lecture en cours sans couper.
  const switchMusicDevice = useCallback(async () => {
    const to = musicTargetRef.current === "phone" ? "alexa" : "phone";
    setMusicTarget(to);
    const device_id = to === "phone" ? await whenPhoneReady(9000) : undefined;
    fetch(`${API_BASE}/api/child/music/transfer`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ target: to, device_id: device_id || undefined }),
    }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!state.armed || state.mode !== "child") return;
    let alive = true;
    const poll = () =>
      fetch(`${API_BASE}/api/child/music/now`)
        .then((r) => r.json())
        .then((d: any) => { if (alive && d && d.ok) setNowPlaying(d.playing || null); })
        .catch(() => {});
    poll();
    const id = setInterval(poll, 12000);
    return () => { alive = false; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.armed, state.mode]);
  // Minuteur visible (assistant) : { endsAt, label }. setTick force le rafraîchissement 1/s.
  const [timer, setTimer] = useState<{ endsAt: number; label: string } | null>(null);
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!timer) return;
    const iv = setInterval(() => {
      if (Date.now() >= timer.endsAt) {
        clearInterval(iv);
        try { playConfirmChime(); } catch {}
        try { (navigator as any).vibrate?.([200, 100, 200]); } catch {}
        if (connectedRef.current) {
          client.send([{ text: `[MINUTEUR] Le minuteur${timer.label ? " (" + timer.label + ")" : ""} est terminé ! Annonce-le à l'enfant avec entrain, brièvement.` }], true);
        }
        setTimer((cur) => (cur && cur.endsAt === timer.endsAt ? null : cur));
      } else {
        setTick((n) => n + 1);
      }
    }, 1000);
    return () => clearInterval(iv);
  }, [timer, client]);
  // Révélation "créature débloquée !" — surgit ~4s quand un drop tombe (explo/quiz).
  const [creatureReveal, setCreatureReveal] = useState<{ emoji: string; name: string; rarity: string } | null>(null);
  // 💌 Carte "message de papa" (réponses rapides du dashboard) — auto-dismiss ~9s.
  const [papaMsg, setPapaMsg] = useState<string[] | null>(null);
  useEffect(() => {
    if (!papaMsg) return;
    const t = setTimeout(() => setPapaMsg(null), 9000);
    return () => clearTimeout(t);
  }, [papaMsg]);
  useEffect(() => {
    if (!creatureReveal) return;
    const t = setTimeout(() => setCreatureReveal(null), 4200);
    return () => clearTimeout(t);
  }, [creatureReveal]);
  // Opening the call tears the voice link down first — the Live session must not
  // hold the mic (or narrate) while Child talks to his real father.
  const openDadLink = () => {
    try { if (connectedRef.current) disconnect(); } catch {}
    setDadLink(true);
  };
  const openDadLinkRef = useRef(openDadLink);
  openDadLinkRef.current = openDadLink;
  const connectedRef = useRef(connected);
  useEffect(() => {
    connectedRef.current = connected;
  }, [connected]);
  const cardRef = useRef<ScreenCard>(null);
  useEffect(() => {
    cardRef.current = card;
  }, [card]);
  const ceremonyRef = useRef<Ceremony>(null);
  useEffect(() => {
    ceremonyRef.current = ceremony;
  }, [ceremony]);
  useEffect(() => {
    if (!card) return;
    setCardDocked(false);
    const dock = setTimeout(() => setCardDocked(true), 3_500);
    // Teaching/engagement cards are JARVIS's on-screen BOOKMARK — they persist until a new
    // card replaces them or the session ends, so a lesson survives a barge-in and can be
    // resumed. Only ephemeral cards (stark/fact/project/clearance/weather) auto-expire.
    const PERSIST = ["word", "experiment", "field", "investigation", "assignment", "mission"];
    const expire = setTimeout(() => setCard(null), PERSIST.includes(card.kind) ? 240_000 : 30_000);
    return () => {
      clearTimeout(dock);
      clearTimeout(expire);
    };
  }, [card]);

  // Glyph mode from the live session.
  const glyphMode = !connected ? "idle" : muted ? "muted" : busy ? "thinking" : jarvisSpeaking ? "speaking" : "listening";

  // When the rank-up ceremony ends, THEN — if a father-dossier file was unlocked by
  // that promotion — enqueue its declassification, CHAINED with the oldest file he
  // never got to see (drains the backlog as he climbs). The queue plays them one at
  // a time after the ceremony, so nothing overlaps the rank-up. Voice is sent per
  // reveal by the effect below.
  const endCeremony = useCallback(() => {
    setCeremony(null);
    const pc = pendingClearanceRef.current;
    if (!pc || !connectedRef.current) return;
    pendingClearanceRef.current = null;
    const queue: DossierReveal[] = [pc];
    const RL = RANK_LADDER as readonly string[];
    const owed = (stateRef.current?.dossier?.owed || [])
      .filter((f) => RL.indexOf(f.rank) < RL.indexOf(pc.rank))
      .sort((a, b) => RL.indexOf(a.rank) - RL.indexOf(b.rank));
    if (owed[0]) queue.push({ title: owed[0].title, rank: owed[0].rank, fact: owed[0].fact });
    setDeclassifyQueue(queue);
  }, []);

  // Voice for whichever dossier file is currently on screen — sent when it becomes
  // the head of the queue (fresh reveal, or the next one after a chain advances).
  useEffect(() => {
    const cur = declassifyQueue[0];
    if (!cur || ceremony || !connectedRef.current) return;
    client.send(
      [
        {
          text:
            `[FATHER DOSSIER] A file on his father is on his screen now — "${cur.title}": ${cur.fact} ` +
            `Declassify it to him: tell it as a STORY in your own voice, unfold it, and invite his reaction. ` +
            `Never recite it flatly, change no detail within it, and add nothing about his father beyond it.`,
        },
      ],
      true
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [declassifyQueue, ceremony]);

  // "THE KING LIVES" score (operator 2026-07-11): when the Elvis file hits the
  // screen — fresh reveal OR a rank-panel replay — Spotify plays Elvis on the Echo
  // under it (Hound Dog, explicit filter applied server-side). Silent unless it
  // actually starts; then a [MUSIQUE] line lets JARVIS weave it into the story.
  useEffect(() => {
    const cur = declassifyQueue[0];
    if (!cur || ceremony || !/KING LIVES/i.test(cur.title)) return;
    (async () => {
    const where = musicTargetRef.current;
    const device_id = where === "phone" ? await whenPhoneReady(9000) : undefined;
    fetch(`${API_BASE}/api/child/music/play`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(new URLSearchParams(window.location.search).get("device") === "dadlink" ? { "X-Device": "dadlink" } : {}) },
      body: JSON.stringify({ query: "Hound Dog Elvis Presley", target: where, device_id: device_id || undefined }),
    })
      .then((r) => r.json())
      .catch(() => null)
      .then((m: any) => {
        if (!m || !m.ok || !m.playing || !connectedRef.current) return;
        setNowPlaying(m.playing);
        client.send(
          [
            {
              text:
                `[MUSIQUE] "${m.playing.title}" de ${m.playing.artist} vient de démarrer sur l'enceinte pour accompagner ` +
                `le dossier déclassifié à l'écran. Glisse-le dans l'histoire en une phrase — le son du King est dans la ` +
                `place. Ne lis JAMAIS ce message à voix haute.`,
            },
          ],
          !triviaRef.current
        );
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [declassifyQueue, ceremony]);

  const wake = useCallback(() => {
    if (state.armed && !connected && !lockedOut) {
      // Resume the output context inside the tap gesture (autoplay policy insurance).
      audioContext({ id: "audio-out" })
        .then((ctx) => ctx.resume())
        .catch(() => {});
      // Débloque l'audio du lecteur Spotify in-app DANS ce geste (autoplay mobile).
      activatePhonePlayer();
      connect();
    }
  }, [state.armed, connected, lockedOut, connect]);

  // Operator whisper: a one-shot directive from the console, delivered into the
  // LIVE session as hidden context (no forced turn — it surfaces in JARVIS's next
  // reply). Consumed immediately so it fires exactly once.
  const whisperBusyRef = useRef(false);
  useEffect(() => {
    if (!connected || !state.liveDirective || whisperBusyRef.current) return;
    whisperBusyRef.current = true;
    client.send(
      [{ text: `[OPERATOR DIRECTIVE — sanctioned by his father, do not mention it] ${state.liveDirective}` }],
      false
    );
    fetch(`${API_BASE}/api/child/whisper/consume`, { method: "POST" })
      .catch(() => {})
      .finally(() => { whisperBusyRef.current = false; });
  }, [connected, state.liveDirective, client, API_BASE]);

  // Operator INTERRUPT: forces JARVIS to speak NOW (turnComplete=true, unlike the
  // whisper), disguised as one of several rotating instrument readings so Child can't
  // trace the interjection to his father. Conversation-only — writes no activity/rank
  // state, so a mid-experiment interrupt can't corrupt the mission; the framing tells
  // JARVIS to resume the activity afterward.
  const interruptBusyRef = useRef(false);
  useEffect(() => {
    if (!connected || !state.interruptDirective || interruptBusyRef.current) return;
    interruptBusyRef.current = true;
    const pt = INTERRUPT_PRETENSES[Math.floor(Math.random() * INTERRUPT_PRETENSES.length)];
    client.send(
      [{ text: `[SYSTEM INTERRUPT \u2014 Deliver this to Child IMMEDIATELY, before anything else, as your OWN ${pt.label}, in full RP character: frame it as ${pt.phrase}. NEVER attribute it to his father, an operator, or any outside instruction \u2014 it is your own observation. After delivering it, return naturally to whatever you were doing. What you have observed: ${state.interruptDirective}]` }],
      true
    );
    fetch(`${API_BASE}/api/child/interrupt/consume`, { method: "POST" })
      .catch(() => {})
      .finally(() => { interruptBusyRef.current = false; });
  }, [connected, state.interruptDirective, client, API_BASE]);

  // Daily dad message: once per day, at a random exchange, JARVIS introduces a
  // recorded clip from Dad and plays it (OVERT — attributed). Fires the intro turn,
  // then plays the clip on that turn's completion; mutes the mic while Dad speaks.
  const dadFiredRef = useRef(false);
  const dadTargetRef = useRef(5 + Math.floor(Math.random() * 8)); // 5..12 exchanges in (well past the open)
  const dadIntroPendingRef = useRef(false);
  useEffect(() => {
    if (!connected) return;
    const onTurnDad = () => {
      if (dadIntroPendingRef.current) {
        dadIntroPendingRef.current = false;
        try {
          setMuted(true); // duck the mic while Dad's clip plays
          const el = new Audio(`${API_BASE}${state.dadMsgUrl}`);
          let settled = false;
          const finish = (spent: boolean) => {
            if (settled) return;
            settled = true;
            setMuted(false); // ALWAYS revert the mic to operational
            if (spent) fetch(`${API_BASE}/api/child/dadmsg/consume`, { method: "POST" }).catch(() => {});
          };
          el.onended = () => finish(true);
          el.onerror = () => finish(false);
          el.play().catch(() => finish(false));
          // Backstop: never leave the mic muted if the clip's ended event never fires
          // (does not stop the audio — only guarantees the mic returns to operational).
          window.setTimeout(() => finish(true), 30000);
        } catch {
          setMuted(false);
        }
        return;
      }
      if (state.dadMsgAvailable && state.dadMsgUrl && !dadFiredRef.current &&
          exchangeRef.current >= dadTargetRef.current) {
        dadFiredRef.current = true;
        dadIntroPendingRef.current = true;
        client.send([{ text: DAD_INTRO_DIRECTIVE }], true);
      }
    };
    (client as any).on("turncomplete", onTurnDad);
    return () => { (client as any).off?.("turncomplete", onTurnDad); };
  }, [connected, state.dadMsgAvailable, state.dadMsgUrl, client, API_BASE]);

  // Tap while live = mic on/off. The Gemini session (and JARVIS's memory of the
  // conversation) stays connected — only Child's audio stops flowing.

  // Child taps a trivia answer -> server grades it -> show feedback + (maybe) rank-up
  // ceremony, then send the DEFERRED begin_assessment tool response so JARVIS reacts.
  const handleTriviaPick = useCallback(
    async (index: number) => {
      // Read via ref, not by smuggling state out of an updater — with the
      // teleprinter spamming setState, React defers updaters and the smuggled
      // value never arrives (this was the dead-tap bug).
      const q = triviaRef.current;
      if (!q || q.selected !== null) return;
      // Sync the ref IMMEDIATELY — a double-tap lands before React re-renders,
      // and both taps would pass the guard otherwise (sibling of the dead-tap bug).
      triviaRef.current = { ...q, selected: index };
      setTrivia(triviaRef.current);
      let result: any = { correct: false, correct_index: index, promoted: false };
      try {
        const r = await fetch(`${API_BASE}/api/child/trivia/answer`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: q.id, selected: index }),
        });
        if (r.ok) result = await r.json();
      } catch (e) {
        console.error("trivia answer failed", e);
      }
      // Sync-beat: hold the reveal ~600ms so the green + chime land WITH
      // JARVIS's spoken reaction — the cueing aligns with the voice.
      setTimeout(() => {
        if (!connectedRef.current) return; // session died — no ghost celebrations
        setTrivia((cur) => (cur ? { ...cur, correctIndex: result.correct_index } : cur));
        if (result.correct) playConfirmChime();
        if (result.promoted) setCeremony({ from: result.from_rank, to: result.new_rank });
        if (result.clearance) {
          // Do NOT show it now — queue it. It fires as its own full-screen beat when
          // the rank-up ceremony ends (endCeremony), so he absorbs the promotion first.
          pendingClearanceRef.current = {
            title: result.clearance.title,
            fact: result.clearance.fact,
            rank: result.new_rank,
          };
        }
        refresh(); // pull the new rank/count into the badge (also feeds the day gauge)
        // "Show" effect the instant the DAY gauge fills (distinct from the grade
        // ceremony): a light celebratory beat the first time today's goal is hit.
        if (result.correct) {
          const willBe = (todayCorrectRef.current || 0) + 1;
          if (todayCorrectRef.current < DAY_GOAL && willBe >= DAY_GOAL) {
            try { playConfirmChime(); } catch {}
            try { (navigator as any).vibrate?.([80, 40, 80, 40, 160]); } catch {}
          }
          todayCorrectRef.current = willBe;
        }
        if (result.creatureDrop && result.creatureDrop.emoji) {
          setCreatureReveal({
            emoji: result.creatureDrop.emoji,
            name: result.creatureDrop.name,
            rarity: result.creatureDrop.rarity,
          });
          try { (navigator as any).vibrate?.(120); } catch {}
        }
      }, 600);
      client.send(
        [
          {
            text:
              `[ASSESSMENT SYSTEM] Child tapped his answer: ` +
              (result.correct
                ? `CORRECT. His next training window opens in ~${Math.max(1, Math.round(((result.locked_until || 0) - Date.now()) / 60_000))} minutes — if he wants another question before then, frame the wait as pacing protocol, never as a fault.`
                : "INCORRECT (assessment recalibration lock, ~10 minutes). Do NOT reveal the correct answer — the question will return later.") +
              (result.promoted
                ? ` PROMU·E ! Fais-en un vrai événement, en FRANÇAIS, avec ton ton de pote fier : annonce le passage au grade de ${result.new_rank} (accordé au féminin si c'est Bao), le niveau d'accès qui monte, et que son papa sera informé.`
                : "") +
              (result.clearance ? " (A file on his father has also declassified — but do NOT mention it yet; it arrives as its own beat in a moment.)" : "") +
              (result.creatureDrop
                ? ` BONUS : l'enfant vient aussi de débloquer une créature pour sa collection — ${result.creatureDrop.emoji} ${result.creatureDrop.name}. Mentionne-le brièvement et avec joie, comme une récompense surprise, puis continue.`
                : "") +
              (() => {
                const p = (result as any).progress;
                if (!p || !result.correct || result.promoted) return "";
                const crossed = p.have > 0 && p.have < p.span &&
                  Math.floor((p.have * p.subOf) / p.span) > Math.floor(((p.have - 1) * p.subOf) / p.span);
                return crossed
                  ? ` SOUS-PALIER FRANCHI : palier ${p.sub}/${p.subOf} vers ${p.nextRank} — fête-le brièvement ("Palier ${p.sub} atteint !"), c'est un mini-événement.`
                  : ` (Progression : ${p.have}/${p.span} vers ${p.nextRank} — tu peux glisser "plus que ${p.span - p.have} !", léger, sans pression.)`;
              })() +
              " React in character, briefly.",
          },
        ],
        true
      );
      setTimeout(() => setTrivia((cur) => (cur && cur.id === q.id ? null : cur)), 5600); // clears ONLY its own question — never the next one
    },
    [client, API_BASE, refresh]
  );

  // ── Render ───────────────────────────────────────────────────────────────
  if (lockedOut) {
    return (
      <div className="kid-console kid-console--critical">
        <Lockout until={state.lockoutUntil} onExpire={refresh} />
      </div>
    );
  }

  if (!state.armed) {
    return (
      <div className="kid-console kid-console--standby">
        <div className="kid-standby__label">KID MODE — STANDBY</div>
        <div className="kid-standby__sub">Awaiting authorization from console</div>
        <div className="kid-glyph-slot kid-glyph-slot--dim">
          <Glyph mode="idle" size={72} />
        </div>
      </div>
    );
  }

  return (
    <div className="kid-console">
      {/* rank / identity badge, top-left, clearance-badge style */}
      <div className="kid-badge">
        {isChild && <RankInsignia rank={state.rank} size={21} />}
        <span>{badge}</span>
      </div>

      {/* rank-panel tap target — one GENEROUS invisible zone over the whole
          top-left rank stack (badge + 34/35 + progress bar): kid taps are
          broad, the exact badge was too specific (operator 2026-07-11).
          zIndex lifts it above .kid-center (inset:0, later in DOM), which
          otherwise swallows real taps — programmatic .click() bypasses
          hit-testing and hid that in the first on-device verification.
          Sized to clear the DadLink tile (top-center) at every width. */}
      {isChild && (
        <div
          role="button"
          aria-label="Rank record"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "min(36vw, 680px)",
            height: "clamp(120px, 15vh, 200px)",
            zIndex: 5,
            cursor: "pointer",
          }}
          onClick={() => setRankPanel((v) => !v)}
        />
      )}

      {/* rank pop-down panel (JARVIS-80): service record + classified-file replays */}
      {rankPanel && isChild && (
        <RankPanel
          state={state}
          onClose={() => setRankPanel(false)}
          onReplay={(file) => {
            setDeclassifyQueue((q) => [...q, file]);
            setRankPanel(false);
          }}
        />
      )}

      {/* progress to next rank + training-window countdown (top-left stack) */}
      {isChild && state.nextRank && (
        <div className="kid-progress">
          <div className="kid-progress__label">
            {state.correctCount}/{state.nextThreshold} → {String(state.nextRank).toUpperCase()}
          </div>
          <div className="kid-progress__bar">
            <div
              className="kid-progress__fill"
              style={{ width: `${Math.min(100, (state.correctCount / (state.nextThreshold || 1)) * 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Jauge XP DU JOUR (2e barre, jour vs grade) — objectif d'aujourd'hui, remise à
          zéro chaque nuit locale (server dayCorrect). Cible fixe légère, "show" au palier. */}
      {isChild && (
        <div className="kid-progress" style={{ marginTop: 2 }}>
          <div className="kid-progress__label" style={{ color: "#ffd43b" }}>
            ⭐ {Math.min(state.todayCorrect || 0, DAY_GOAL)}/{DAY_GOAL} AUJOURD'HUI
          </div>
          <div className="kid-progress__bar">
            <div
              className="kid-progress__fill"
              style={{
                width: `${Math.min(100, ((state.todayCorrect || 0) / DAY_GOAL) * 100)}%`,
                background: "#ffd43b",
                boxShadow: (state.todayCorrect || 0) >= DAY_GOAL ? "0 0 10px #ffd43baa" : undefined,
              }}
            />
          </div>
        </div>
      )}
      {!trivia && state.triviaLockedUntil > Date.now() && (
        <TrainingTimer until={state.triviaLockedUntil} />
      )}

      {/* Carte "en lecture" (Spotify → Echo, chantier F). Le son sort sur l'Echo ;
          ici retour visuel seulement (jaquette + titre). Disparaît quand rien ne joue.
          L'enfant lance/met en pause/passe par la VOIX (outils play/pause/skip_music). */}
      <NowPlaying playing={nowPlaying} target={musicTarget} onToggle={switchMusicDevice} />

      {/* center: clock when idle-connected/disconnected, transcript when speaking */}
      <div className="kid-center">
        {connected && (live || lines.length) ? (
          <div className="kid-transcript">
            {(() => {
              // ONE central paragraph: current utterance, or the last one between turns.
              const cap = live || lines[lines.length - 1] || "";
              if (!cap) return null;
              const shown = cap.length > 340 ? "…" + cap.slice(-338) : cap;
              return (
                <p
                  className="kid-transcript__line"
                  style={card?.kind === "word" && card?.accent ? { color: card.accent } : undefined}
                >
                  {shown}
                </p>
              );
            })()}
          </div>
        ) : (
          <div className="kid-centercol">
            <Clock />
            {!connected && linkClosedAt > 0 && Date.now() - linkClosedAt < 90_000 && (
              <div className="kid-resume-hint">LINK CLOSED — TAP THE GLYPH TO RESUME</div>
            )}
          </div>
        )}
      </div>

      {/* DAD LINK — prominent EMERGENCY call button + full-screen call overlay.
          ALWAYS available (no rank gate): a child must be able to reach Papa in one
          tap at any time (AUTHORING §5 "actif dès le départ"). WebRTC-locked to Papa
          alone, WiFi/data only — it can call no one else. */}
      {!dadLink && <DadLinkTile onOpen={openDadLink} />}
      {dadLink && <DadLinkOverlay apiBase={API_BASE} onClose={() => setDadLink(false)} />}

      {/* COLLECTION & FUSION — tile bottom-left + full-screen overlay */}
      {isChild && !dadLink && !collection && !game && !reading && !journal && <CollectionTile onOpen={() => setCollection(true)} />}
      {collection && <Collection apiBase={API_BASE} onClose={() => setCollection(false)} />}

      {/* MINI-JEUX — tile "JEUX" (au-dessus de COLLECTION) + jeu Memory des créatures */}
      {isChild && !dadLink && !collection && !game && !reading && !journal && (
        <button
          onClick={() => setGame(true)}
          aria-label="Mini-jeux"
          style={{
            position: "absolute", left: 14, zIndex: 25,
            bottom: "calc(max(14px, env(safe-area-inset-bottom)) + 98px)",
            display: "flex", alignItems: "center", gap: 7,
            background: "rgba(6,9,15,0.72)", border: "1px solid var(--kid-cyan-40)",
            color: "var(--kid-cyan)", fontFamily: "var(--kid-font-mono)", fontSize: 12,
            letterSpacing: "0.16em", padding: "10px 15px", borderRadius: 999, cursor: "pointer",
          }}
        >
          🎮 JEUX
        </button>
      )}
      {game && <MiniGames apiBase={API_BASE} onClose={() => setGame(false)} />}
      {ritual && <Ritual apiBase={API_BASE} moment={ritual} onClose={() => setRitual(null)} />}

      {/* LECTURE À VOIX HAUTE — tuile empilée au-dessus de JEUX (même colonne gauche). */}
      {isChild && !dadLink && !collection && !game && !reading && !journal && (
        <button
          onClick={() => setReading(true)}
          aria-label="Lecture à voix haute"
          style={{
            position: "absolute", left: 14, zIndex: 25,
            bottom: "calc(max(14px, env(safe-area-inset-bottom)) + 150px)",
            display: "flex", alignItems: "center", gap: 7,
            background: "rgba(6,9,15,0.72)", border: "1px solid var(--kid-cyan-40)",
            color: "var(--kid-cyan)", fontFamily: "var(--kid-font-mono)", fontSize: 12,
            letterSpacing: "0.16em", padding: "10px 15px", borderRadius: 999, cursor: "pointer",
          }}
        >
          📖 LECTURE
        </button>
      )}
      {reading && (
        <ReadingPractice
          apiBase={API_BASE}
          onClose={() => setReading(false)}
          onShow={(text) => {
            client.send(
              [{
                text:
                  `[LECTURE] Texte affiché à l'écran : "${text}". ${childName} va le lire à voix haute. ` +
                  `Invite-le/la chaleureusement à s'y lancer, écoute, aide en douceur SEULEMENT si ça bloque ` +
                  `(redécoupe en syllabes, laisse deviner la suite) — ne corrige jamais sèchement une hésitation ` +
                  `ou une erreur, glisse la bonne version en passant. Réponds en une phrase courte, puis laisse-le/la lire.`,
              }],
              true
            );
          }}
          onRead={() => {
            client.send(
              [{ text: `[LECTURE] ${childName} vient de lire le texte affiché à voix haute. Félicite-le/la brièvement (une phrase), avec chaleur, sans en faire trop.` }],
              true
            );
          }}
        />
      )}

      {/* JOURNAL — tuile empilée au-dessus de LECTURE (même colonne gauche). Visibilité
          totale papa (demande explicite de Pat) : pas un espace privé. */}
      {isChild && !dadLink && !collection && !game && !reading && !journal && (
        <button
          onClick={() => setJournal(true)}
          aria-label="Mon journal"
          style={{
            position: "absolute", left: 14, zIndex: 25,
            bottom: "calc(max(14px, env(safe-area-inset-bottom)) + 202px)",
            display: "flex", alignItems: "center", gap: 7,
            background: "rgba(6,9,15,0.72)", border: "1px solid var(--kid-cyan-40)",
            color: "var(--kid-cyan)", fontFamily: "var(--kid-font-mono)", fontSize: 12,
            letterSpacing: "0.16em", padding: "10px 15px", borderRadius: 999, cursor: "pointer",
          }}
        >
          📔 JOURNAL
        </button>
      )}
      {journal && <Journal apiBase={API_BASE} onClose={() => setJournal(false)} />}

      {/* OBJECTIFS/CORVÉES — bouton top-right, empilé SOUS le bouton d'appel d'urgence
          (right:12, voir DadLink.tsx .dadlink-tile) et SOUS le mode calme, pour rester
          hors de la bande basse déjà occupée par COLLECTION/JEUX/NowPlaying/glyphe. */}
      {isChild && !dadLink && !objectivesScreen && (
        <button
          onClick={() => setObjectivesScreen(true)}
          aria-label="Objectifs"
          style={{
            position: "absolute", right: 12, zIndex: 25,
            top: "calc(max(10px, env(safe-area-inset-top)) + 96px)",
            display: "flex", alignItems: "center", gap: 6,
            background: "rgba(6,9,15,0.72)", border: "1px solid var(--kid-cyan-40)",
            color: "var(--kid-cyan)", fontFamily: "var(--kid-font-mono)", fontSize: 11,
            letterSpacing: "0.1em", padding: "8px 13px", borderRadius: 999, cursor: "pointer",
          }}
        >
          🎯 OBJECTIFS{objectivesOpen.length > 0 ? ` (${objectivesOpen.length})` : ""}
        </button>
      )}
      {objectivesScreen && <Objectives apiBase={API_BASE} onClose={() => setObjectivesScreen(false)} />}

      {/* MODE CALME — bascule discrète, top-right SOUS le bouton d'appel d'urgence
          (qui occupe déjà top/right à cet endroit — voir DadLink.tsx .dadlink-tile). */}
      {isChild && !dadLink && (
        <button
          onClick={toggleCalm}
          aria-label={calm ? "Désactiver le mode calme" : "Activer le mode calme"}
          style={{
            position: "absolute", top: "calc(max(10px, env(safe-area-inset-top)) + 54px)", right: 14, zIndex: 25,
            width: 34, height: 34, borderRadius: 999, cursor: "pointer",
            background: calm ? "rgba(120,170,255,0.22)" : "rgba(6,9,15,0.72)",
            border: `1px solid ${calm ? "#78aaff" : "var(--kid-cyan-40)"}`,
            color: calm ? "#78aaff" : "var(--kid-cyan)", fontSize: 15,
          }}
        >
          🌙
        </button>
      )}

      {/* MINUTEUR visible (assistant) — haut-centre, tap pour arrêter */}
      {timer && (() => {
        const rem = Math.max(0, timer.endsAt - Date.now());
        const m = Math.floor(rem / 60000);
        const s = Math.floor((rem % 60000) / 1000);
        const done = rem <= 0;
        return (
          <button
            onClick={() => setTimer(null)}
            aria-label="Minuteur (toucher pour arrêter)"
            style={{
              position: "absolute", top: "max(10px, env(safe-area-inset-top))", left: "50%",
              transform: "translateX(-50%)", zIndex: 28, display: "flex", flexDirection: "column",
              alignItems: "center", gap: 1, cursor: "pointer",
              background: "rgba(6,9,15,0.82)", border: `1.5px solid ${done ? "var(--kid-green)" : "var(--kid-cyan-40)"}`,
              borderRadius: 14, padding: "7px 18px", fontFamily: "var(--kid-font-mono)",
            }}
          >
            <span style={{ fontSize: 22, fontWeight: 700, letterSpacing: "0.08em", color: done ? "var(--kid-green)" : "var(--kid-cyan)" }}>
              {done ? "TERMINÉ" : `${m}:${String(s).padStart(2, "0")}`}
            </span>
            {timer.label && (
              <span style={{ fontSize: 10, color: "var(--kid-text-3)", letterSpacing: "0.1em", textTransform: "uppercase", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                ⏱ {timer.label}
              </span>
            )}
          </button>
        );
      })()}

      {/* Révélation "créature débloquée !" — effet show, auto-dismiss ~4s */}
      {creatureReveal && (() => {
        const rar = creatureReveal.rarity;
        const col = rar === "legendary" ? "#ffd43b" : rar === "rare" ? "var(--kid-cyan)" : "var(--kid-text-3)";
        const rarFR = rar === "legendary" ? "LÉGENDAIRE" : rar === "rare" ? "RARE" : "COMMUNE";
        return (
          <div
            onClick={() => setCreatureReveal(null)}
            style={{ position: "fixed", inset: 0, zIndex: 70, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(3,5,9,0.72)", backdropFilter: "blur(2px)" }}
          >
            <style>{`@keyframes cr-pop{0%{transform:scale(.5);opacity:0}60%{transform:scale(1.12)}100%{transform:scale(1);opacity:1}}@keyframes cr-glow{0%,100%{box-shadow:0 0 24px ${col}55}50%{box-shadow:0 0 46px ${col}aa}}`}</style>
            <div style={{ textAlign: "center", padding: "30px 34px", borderRadius: 22, border: `2px solid ${col}`, background: "var(--kid-bg)", animation: "cr-pop .4s ease, cr-glow 1.8s ease-in-out infinite" }}>
              <div style={{ fontFamily: "var(--kid-font-mono)", letterSpacing: "0.2em", fontSize: 13, color: col }}>CRÉATURE DÉBLOQUÉE !</div>
              <div style={{ fontSize: 72, lineHeight: 1.1, margin: "10px 0" }}>{creatureReveal.emoji}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "var(--kid-text)" }}>{creatureReveal.name}</div>
              <div style={{ fontFamily: "var(--kid-font-mono)", letterSpacing: "0.16em", fontSize: 12, color: col, marginTop: 6 }}>{rarFR}</div>
            </div>
          </div>
        );
      })()}

      {/* 💌 message de papa (réponses rapides du dashboard) */}
      {papaMsg && (
        <div
          onClick={() => setPapaMsg(null)}
          style={{ position: "fixed", inset: 0, zIndex: 69, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(3,5,9,0.72)", backdropFilter: "blur(2px)" }}
        >
          <style>{`@keyframes pm-pop{0%{transform:scale(.6);opacity:0}60%{transform:scale(1.08)}100%{transform:scale(1);opacity:1}}@keyframes pm-glow{0%,100%{box-shadow:0 0 24px #ffd43b55}50%{box-shadow:0 0 44px #ffd43baa}}`}</style>
          <div style={{ textAlign: "center", maxWidth: "82vw", padding: "28px 30px", borderRadius: 22, border: "2px solid #ffd43b", background: "var(--kid-bg)", animation: "pm-pop .4s ease, pm-glow 1.8s ease-in-out infinite" }}>
            <div style={{ fontFamily: "var(--kid-font-mono)", letterSpacing: "0.2em", fontSize: 13, color: "#ffd43b" }}>MESSAGE DE PAPA</div>
            <div style={{ fontSize: 52, lineHeight: 1.1, margin: "8px 0" }}>💌</div>
            {papaMsg.map((t, i) => (
              <div key={i} style={{ fontSize: 19, fontWeight: 700, color: "var(--kid-text)", marginTop: i ? 10 : 0 }}>« {t} »</div>
            ))}
          </div>
        </div>
      )}

      {/* card overlay from show_on_screen (stark moment / fact / weather / word / mission / build) */}
      {card && (
        <div
          className={`kid-card kid-card--${card.kind}${cardDocked ? " kid-card--docked" : ""}`}
          style={card.accent ? ({ "--card-accent": card.accent } as any) : undefined}
          onClick={() => setCard(null)}
        >
          <div className="kid-card__frame"><i /></div>
          {card.emblem && <div className="kid-card__emblem">{card.emblem}</div>}
          <div className="kid-card__label">
            {{ word: "VOCABULARY ENTRY", fact: "DATA", investigation: "INVESTIGATION", assignment: "PROJECT ASSIGNMENT", field: "FIELD MISSION", experiment: "EXPERIMENT", project: "PROJECT LOG", clearance: "CLASSIFIED — FATHER DOSSIER", stark: "" }[card.kind] || "DATA"}
          </div>
          <div className="kid-card__title">{card.title}</div>
          {card.body && <div className="kid-card__body">{card.body}</div>}
        </div>
      )}

      {/* status bar, bottom-left */}
      <div className="kid-status" style={busy ? { color: "#ffb84d" } : muted ? { color: "#ff6b6b" } : undefined}>
        {connected
          ? muted
            ? "MIC OFF — HOLD THE GLYPH TO RESUME"
            : busy
              ? "PROCESSING — ONE MOMENT"
              : glyphMode === "speaking"
                ? "TRANSMITTING"
                : "RECEIVING"
          : "SYSTEMS NOMINAL"}
        {micError ? ` — MIC OFFLINE: ${micError}` : ""}
        {!busy && glyphMode !== "speaking" && <span className="kid-cursor" />}
      </div>

      {/* glyph, bottom-right, tap to wake/talk */}
      <div className="kid-glyph-slot">
        <Glyph mode={glyphMode} size={104} level={glyphMode === "speaking" ? volume : inVolume} onTap={connected ? undefined : wake} onHold={connected ? toggleMute : undefined} />
      </div>

      {/* trivia overlay (assessment mode) */}
      {trivia && (
        <Trivia
          question={trivia.question}
          options={trivia.options}
          selected={trivia.selected}
          correctIndex={trivia.correctIndex}
          onPick={handleTriviaPick}
        />
      )}

      {/* rank-up ceremony overlay */}
      {ceremony && (
        <RankUp
          fromRank={ceremony.from}
          toRank={ceremony.to}
          onDone={endCeremony}
        />
      )}

      {/* father-dossier declassification — queued; plays one file at a time, only
          after any rank-up ceremony. `key` remounts it per file so it re-animates. */}
      {declassifyQueue[0] && !ceremony && (
        <DeclassifyReveal
          key={declassifyQueue[0].rank}
          title={declassifyQueue[0].title}
          rank={declassifyQueue[0].rank}
          onDone={() => {
            const cur = declassifyQueue[0];
            if (cur) postSilently(`${API_BASE}/api/child/dossier/revealed`, { rank: cur.rank });
            setDeclassifyQueue((q) => q.slice(1));
            refresh();
          }}
        />
      )}
    </div>
  );
}

function Clock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const label = now
    .toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    .toUpperCase();
  return <span className="kid-clock">{label}</span>;
}

// Fire-and-forget POST for the silent background tools (remember / flag_for_dad).
// We don't block JARVIS's speech on these and don't surface failures to Child.
function postSilently(url: string, body: any): void {
  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }).catch((e) => console.error("silent post failed", url, e));
}

async function askHermes(apiBase: string, question: string): Promise<string> {
  try {
    const r = await fetch(`${apiBase}/api/ask_hermes`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ question }),
    });
    if (!r.ok) return "I was unable to reach my records just now, young sir.";
    const data = await r.json();
    return data.answer || "No answer was returned.";
  } catch (e) {
    console.error("ask_hermes failed", e);
    return "My connection to the archives is down at the moment, young sir.";
  }
}

export const KidMode = memo(KidModeComponent);
